import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { openAuthedFile, isImage } from "@/lib/files";
import AuthedImage from "@/components/AuthedImage";
import PasteZone from "@/components/PasteZone";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, FileText, Loader2, Eye } from "lucide-react";

// Upload / paste / drag-drop documents + image thumbnails, scoped by pointId
// (pointId can be composite, e.g. "4::azba", to attach files to a sub-item).
export default function FileAttachments({ briefingId, pointId, compact = false, title = "Documents joints" }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/briefings/${briefingId}/files`);
      setFiles(data.filter((f) => String(f.point_id) === String(pointId)));
    } catch {
      setFiles([]);
    }
  }, [briefingId, pointId]);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("point_id", String(pointId));
    setBusy(true);
    try {
      await api.post(`/briefings/${briefingId}/files`, fd);
      toast.success("Document ajouté");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => { await uploadFile(e.target.files?.[0]); e.target.value = ""; };
  const remove = async (fid) => {
    try { await api.delete(`/briefings/${briefingId}/files/${fid}`); await load(); }
    catch { toast.error("Suppression impossible"); }
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) uploadFile(f); };

  const images = files.filter((f) => isImage(f.content_type));
  const docs = files.filter((f) => !isImage(f.content_type));

  return (
    <div data-testid={`attachments-${pointId}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)} onDrop={onDrop}
      className={`rounded-xl border p-3 transition-colors ${drag ? "border-cyan-400 bg-cyan-500/10" : "border-slate-800 bg-slate-900/40"}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400">
          <Paperclip className="h-3.5 w-3.5" /> {title}
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1.5 text-xs font-medium text-cyan-300 hover:bg-cyan-500/20">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Ajouter
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf" className="hidden"
            onChange={onUpload} disabled={busy} data-testid={`upload-input-${pointId}`} />
        </label>
      </div>

      <PasteZone onImage={uploadFile} busy={busy} className="mb-2" />

      {images.length > 0 && (
        <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map((f) => (
            <div key={f.id} data-testid={`file-${f.id}`}
              className="group relative overflow-hidden rounded-lg border border-slate-800">
              <AuthedImage path={`/briefings/${briefingId}/files/${f.id}/download`} alt={f.original_filename}
                onClick={() => openAuthedFile(`/briefings/${briefingId}/files/${f.id}/download`)}
                className="h-24 w-full cursor-pointer object-cover transition-transform duration-200 group-hover:scale-105" />
              <button onClick={() => remove(f.id)} data-testid={`del-file-${f.id}`}
                className="absolute right-1 top-1 rounded-md bg-black/60 p-1 text-slate-200 opacity-0 transition-opacity group-hover:opacity-100 hover:text-rose-300">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <ul className="space-y-1.5">
          {docs.map((f) => (
            <li key={f.id} data-testid={`file-${f.id}`}
              className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
              <FileText className="h-4 w-4 text-cyan-500/70" />
              <span className="flex-1 truncate text-sm text-slate-200">{f.original_filename}</span>
              <span className="font-mono-data text-[10px] text-slate-500">{Math.round((f.size || 0) / 1024)} Ko</span>
              <button onClick={() => openAuthedFile(`/briefings/${briefingId}/files/${f.id}/download`)}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-cyan-300"><Eye className="h-4 w-4" /></button>
              <button onClick={() => remove(f.id)} data-testid={`del-file-${f.id}`}
                className="rounded p-1.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}

      {files.length === 0 && !compact && (
        <p className="text-xs text-slate-500">Aucun document. Glissez un fichier, cliquez sur Ajouter, ou collez une capture (PDF, JPG, PNG · max 10 Mo).</p>
      )}
    </div>
  );
}
